import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@shared/ui/card";
import { Switch } from "@shared/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@shared/ui/alert";
import { CheckCircle, Eye, EyeOff } from "lucide-react";
import { Button } from "@shared/ui/button";
import { toast } from "@shared/hooks/use-toast";
import { DynamicForm } from "@shared/components/DynamicForm";
import {
  useAddPaymentConfig,
  useConnectPaymentMethod,
  useDecryptPspCredentials,
  useUpdatePSPConfig,
  usePSPConfig,
} from "@features/ix/payment/hooks";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { useGlobalUserProfile } from "@shared/hooks/useGlobalUser";
import {
  FormService,
  FormConfig,
  FormField,
} from "@shared/services/FormServices";
import { PageHeader } from "@shared/components/PageHeader";

export const StripeConfigForm = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const CURRENCY = useCurrency();
  const { data: user } = useGlobalUserProfile();
  const { data: pspConfigList, refetch } = usePSPConfig(remoteUtilityId);

  // Extract Stripe specifically
  const stripeData = useMemo(() => {
    if (!pspConfigList) return null;
    const psp = pspConfigList.find(
      (p: any) => p?.organizationName?.toLowerCase() === "stripe",
    );
    if (!psp) return null;
    return {
      id: psp.pspId,
      pspUtilityId: psp.pspUtilityId ?? null,
      title: psp.organizationName,
      description: psp.description ?? "",
      credentials: psp.pspCredential ?? {},
      version: psp.version ?? "",
      environment: psp.environment ?? "",
      apiEndpoint: psp.apiEndpoint ?? "",
      enabled: psp.isActive,
      verificationStatus: psp.verificationStatus ?? "",
    };
  }, [pspConfigList]);

  const [decrypt, setDecrypt] = useState(false);
  const pspUtilityId = stripeData?.pspUtilityId;

  const { mutate: updatePSPConfig } = useUpdatePSPConfig(pspUtilityId);
  const { mutate: addConfig } = useAddPaymentConfig();
  const { mutate: connectPaymentMethod } = useConnectPaymentMethod();
  const { data: decryptedCredentials } = useDecryptPspCredentials(
    pspUtilityId,
    decrypt,
  );

  const fields: FormField[] = useMemo(() => {
    return [
      {
        name: "apiKey",
        label: "API Key",
        type: "text",
        placeholder: "sk_live...",
      },
      {
        name: "secretKey",
        label: "Secret Key",
        type: "text",
        placeholder: "rk_live...",
        customValidation: (value: string) => {
          if (!value || value.trim().length < 2) {
            return "Secret key must be at least 2 characters";
          }
          return true;
        },
      },
      {
        name: "webhook_url",
        label: "Webhook URL",
        type: "text",
        required: false,
        placeholder: "https://api.smart360.io/webhooks/stripe",
        disabled: true,
      },
      {
        name: "webhookSecret",
        label: "Webhook Secret",
        type: "password",
        placeholder: "whsec_...",
      },
      {
        name: "publicKey",
        label: "Public Key",
        type: "text",
        placeholder: "pk_live...",
      },
    ];
  }, []);

  const normalizedCredentials = useMemo(() => {
    if (!stripeData) return {};
    return {
      ...stripeData.credentials,
      webhook_url: stripeData.apiEndpoint,
    };
  }, [stripeData]);

  const initialValues = useMemo(() => {
    return fields.reduce(
      (acc, field) => {
        acc[field.name] = normalizedCredentials[field.name] ?? "";
        return acc;
      },
      {} as Record<string, any>,
    );
  }, [fields, normalizedCredentials]);

  const formInstance = FormService.createForm({
    fields,
    mode: "onSubmit",
    validateOnMount: false,
  });

  useEffect(() => {
    if (!stripeData?.credentials) return;
    Object.entries(initialValues).forEach(([field, value]) =>
      formInstance.setValue(field, value as any),
    );
  }, [formInstance, initialValues, stripeData]);

  const allFilled = fields.every(
    (field) =>
      !field.required ||
      (initialValues?.[field.name] &&
        initialValues[field.name].toString().trim() !== ""),
  );

  const handleTogglePayment = (checked: boolean) => {
    if (stripeData?.verificationStatus !== "Verified") {
      toast({
        title: "Connect the payment method first to activate it.",
      });
      return;
    }
    if (!pspUtilityId) {
      toast({
        title: "Error",
        description: "Please configure the payment details first",
        variant: "destructive",
      });
      return;
    }

    updatePSPConfig({ isActive: checked }, { onSuccess: refetch });
  };

  const handleSubmitSaveConfig = (data: any) => {
    const { webhook_url, ...formValues } = data;

    if (pspUtilityId && allFilled) {
      const editPayload = FormService.getUpdatedFields(initialValues, data);
      updatePSPConfig(
        {
          psp_credentials: { ...editPayload },
        },
        { onSuccess: refetch },
      );
    } else {
      const payload = {
        remote_utility_id: Number(remoteUtilityId),
        currency: CURRENCY,
        psp: stripeData?.id,
        psp_credentials: { ...formValues },
      };
      addConfig(payload, { onSuccess: refetch });
    }
  };

  const handleConnect = () => {
    const baseUrl = import.meta.env.VITE_PAYMENT_API_ENDPOINT;
    const payload = {
      amount: 1,
      redirect_url: `${baseUrl}integration-hub/integration/payments`,
      cancel_url: `${baseUrl}integration-hub/integration/payments`,
      remote_consumer_id: user?.user?.id,
      payment_method: "Card",
      psp_mapping_id: pspUtilityId,
      remote_utility_id: Number(remoteUtilityId),
      description: "It is a billing payment",
      payment_type: "TEST",
      source: 0,
    };

    connectPaymentMethod(payload, {
      onSuccess: (response) => {
        if (response?.url) {
          window.open(response.url, "_blank", "noopener,noreferrer");
        }
      },
    });
  };

  const handleDecryptCredentials = () => {
    if (!decrypt) {
      setDecrypt(true);
    } else {
      Object.entries(initialValues).forEach(([key, value]) =>
        formInstance.setValue(key, value as any),
      );
      setDecrypt(false);
    }
  };

  useEffect(() => {
    if (decrypt && decryptedCredentials) {
      Object.entries(decryptedCredentials).forEach(([field, value]) => {
        formInstance.setValue(field, value as any);
      });
    }
  }, [decryptedCredentials, decrypt, formInstance]);

  const renderCustomFooter = () => (
    <div className="flex flex-col gap-6">
      {stripeData?.verificationStatus?.toLowerCase() === "verified" && (
        <Alert variant="default" className="bg-green-50">
          <CheckCircle className="h-5 w-5 !text-green-500" />
          <AlertTitle className="text-green-600">
            Gateway connected successfully
          </AlertTitle>
          <AlertDescription className="text-green-500">
            Your gateway is properly configured and ready to accept payments.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex gap-4">
        <Button
          type="submit"
          onClick={formInstance.handleSubmit(handleSubmitSaveConfig)}
        >
          {allFilled ? "Edit Configuration" : "Save Configuration"}
        </Button>

        {stripeData?.verificationStatus?.toLowerCase() === "in progress" && (
          <Button type="button" onClick={handleConnect}>
            Connect
          </Button>
        )}

        {stripeData?.verificationStatus?.toLowerCase() === "verified" && (
          <Button type="button" disabled>
            Connected
          </Button>
        )}

        {(stripeData?.verificationStatus?.toLowerCase() === "in progress" ||
          stripeData?.verificationStatus?.toLowerCase() === "verified") &&
          allFilled && (
            <Button type="button" onClick={handleDecryptCredentials}>
              {decrypt ? <EyeOff className="mr-2" /> : <Eye className="mr-2" />}
              {decrypt ? "Hide Keys" : "Decrypt Keys"}
            </Button>
          )}
      </div>
    </div>
  );

  const config: FormConfig = {
    fields,
    showResetButton: false,
    showSubmitButton: false,
    resetOnSubmit: false,
    shouldUnregister: false,
    layout: "grid",
    columns: 2,
    classes: {
      fieldContainer: "flex flex-col gap-1 mb-4",
      error: "text-sm text-red-500 mt-1",
      buttonContainer: "hidden",
    },
  };

  if (!stripeData) {
    return (
      <div className="p-6 text-gray-500">Loading Stripe configuration...</div>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center pb-4 justify-between space-y-0">
        <div>
          <PageHeader
            title={stripeData.title}
            subtitle={stripeData.description}
          />
        </div>
        <Switch
          checked={stripeData.enabled}
          onCheckedChange={handleTogglePayment}
        />
      </CardHeader>
      <CardContent className="pt-8 border-t">
        <DynamicForm
          form={formInstance}
          fields={fields}
          config={config}
          gridColumns={2}
          renderCustomFooter={renderCustomFooter}
          onSubmit={handleSubmitSaveConfig}
        />
      </CardContent>
    </Card>
  );
};
